
import { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { FeatureFlag } from "@/types/settings";

interface FeatureFlagTableProps {
  featureFlags: FeatureFlag[];
  isLoading: boolean;
  onToggleFeatureFlag: (flagId: string, enabled: boolean) => Promise<void>;
}

const FeatureFlagTable = ({ featureFlags, isLoading, onToggleFeatureFlag }: FeatureFlagTableProps) => {
  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-5 py-10 text-center shadow-sm">
        <div className="animate-pulse text-gray-500">Loading feature flags...</div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 bg-gray-50 px-5 py-4">
        <h3 className="text-sm font-semibold text-gray-900">Feature flags</h3>
        <p className="mt-1 text-sm text-gray-500">
          Flag status stays visible next to its description so rollout decisions are easy to verify at a glance.
        </p>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="border-gray-200 hover:bg-transparent">
            <TableHead className="bg-white text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Feature</TableHead>
            <TableHead className="bg-white text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Description</TableHead>
            <TableHead className="bg-white text-right text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {featureFlags.map((flag) => (
            <TableRow key={flag.id} className="border-gray-200 hover:bg-gray-50">
              <TableCell className="font-medium text-gray-900">{flag.name}</TableCell>
              <TableCell className="text-gray-500">{flag.description}</TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-3">
                  <span className="text-sm text-muted-foreground">
                    {flag.enabled ? "Enabled" : "Disabled"}
                  </span>
                  <Switch
                    checked={flag.enabled}
                    onCheckedChange={(checked) => onToggleFeatureFlag(flag.id, checked)}
                    className="data-[state=checked]:bg-whatsapp"
                  />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default FeatureFlagTable;
